/* eslint-disable class-methods-use-this */
import * as Yup from 'yup';
import { FormikProps } from 'formik';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { patchDeath } from '../../../../../global/ducks/person-prc-api';
import { appendDocument, formatDate } from '../../../../../global/helpers/form-data-helper';
import { getRequiredText } from '../../../../../global/helpers/transformer';
import { YesNoIndicators } from '../../../../../global/models/api-model';
import { DemographicAppKeys } from '../../../../../global/models/demographics-model';
import { Person } from '../../../../../global/models/person-model';
import { TcsmEpiApiState } from '../../../../../root/tcsm-model';
import { AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { FormTypes } from '../../../models/form-models';
import { PointOfContactFormModel } from '../../mycpg-midyear/models/mycpg-mid-year-models';
import { DocumentUploadState } from '../../personal-info/document-upload-models';
import { processRequestWithDocumentUpload } from '../../personal-info/document-upload-request';
import { DeathOfChildFormModel } from '../model/death-of-child-form-models';
import { removeChild } from '../../dependents/dependents-slice';


export class DeathOfChildWizard {

	readonly epiContent: TcsmEpiApiState;

	readonly enrollmentContext: AnnualEnrollmentKey;

	readonly app: DemographicAppKeys;

	readonly dispatch: any;

	readonly history: any;

	readonly action: string;

	readonly selectedPerson: Person;

	constructor(epiContent: TcsmEpiApiState, enrollmentContext: AnnualEnrollmentKey, app: DemographicAppKeys, dispatch: any, history: any, action: string, selectedPerson: Person) {
		this.epiContent = epiContent;
		this.enrollmentContext = enrollmentContext;
		this.app = app;
		this.dispatch = dispatch;
		this.history = history;
		this.action = action?.toUpperCase();
		this.selectedPerson = selectedPerson;
	}

	public getPageDescription(): string {

		return this.epiContent?.relationshipsSettingState?.epiContent?.deathOfChildPageDescription;
	}

	public getPageHeader(): string {
		return this.epiContent?.relationshipsSettingState?.epiContent?.deathOfChildPageTitle;
	}


	public isEnrollmentForm(): boolean {
		return this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment || this.enrollmentContext === AnnualEnrollmentKey.participantEnrollment || this.enrollmentContext === AnnualEnrollmentKey.mycpgmidyear;
	}

	public isAnnualEnrollmentForm(): boolean {
		return this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment;
	}

	public getGpsType(): GpsType {
		return GpsType.DEATH_OF_CHILD;
	}

	public getSubmitButtonText() {
		return this.epiContent?.relationshipsSettingState?.epiContent?.saveButtonText;
	}

	public getCancelButtonText() {
		return this.epiContent?.relationshipsSettingState?.epiContent?.cancelButtonText;
	}


	public preventButtonDisableValidation(): boolean {
		return this.isEnrollmentForm();
	}

	public getInitialFormValue(): DeathOfChildFormModel {
		return {
			date_of_death: null,
			checkbox1: false,
			selectedChildId: null,
			first_name: this.selectedPerson?.first_name || '',
			middle_name: this.selectedPerson?.middle_name || '',
			last_name: this.selectedPerson?.last_name || '',
		};
	}

	public getRelationShipSettings() {
		return this?.epiContent?.relationshipsSettingState?.epiContent;
	}

	public getLabelSettings() {
		return this?.epiContent?.labelSettingState?.epiContent;
	}

	public getValidationSchema() {

		const validationSchema = Yup.lazy(() => {

			const labelSetting = this.getLabelSettings();
			const relationshipSettings = this.getRelationShipSettings();

			const schema = Yup.object().shape({
				selectedChildId: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, relationshipSettings?.selectChildLabel)),
				date_of_death: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, relationshipSettings?.dateOfChildDeathLabel))
					.max(new Date(Date.now()), labelSetting.dateFutureError || 'Date cannot be in the future'),

			});


			return schema;
		});

		return validationSchema;

	}

	public onCancel() {
		return this.history.goBack();
	}

	private onDeath(
		formValues: DeathOfChildFormModel,
		formikHelpers: FormikProps<DeathOfChildFormModel>,
		guid: string,
		docUploadState: DocumentUploadState,
		relationShipPath: string,
		setShowPointOfContact: (confirm: boolean) => void,
		pointOfContactFormValues?: PointOfContactFormModel) {

		if (!pointOfContactFormValues) {
			setShowPointOfContact(true);
			return;
		}

		let initialPayload: any = {
			preliminary_date_of_death: formatDate(formValues.date_of_death),
		};

		if (guid && pointOfContactFormValues) {
			initialPayload = {
				...initialPayload,
				'dependent_type': pointOfContactFormValues.dependent_type,
			};

			if (pointOfContactFormValues.is_contact_person === YesNoIndicators.Yes) {
				initialPayload = {
					...initialPayload,
					'contact_phone': `${pointOfContactFormValues.your_contact_phone_country_code} ${pointOfContactFormValues.your_contact_phone_formated}`,
					'contact_email': pointOfContactFormValues.your_contact_email,
					'contact_name': `${formValues.first_name}${formValues.middle_name ? ` ${formValues.middle_name} ` : ' '}${formValues.last_name}`,
					'contact_relationship': pointOfContactFormValues.dependent_type,
				};
			} else {
				initialPayload = {
					...initialPayload,
					'contact_phone': `${pointOfContactFormValues.contact_phone_country_code} ${pointOfContactFormValues.contact_phone_number_formated}`,
					'contact_email': pointOfContactFormValues.contact_email,
					'contact_name': pointOfContactFormValues.full_name_contact,
					'contact_relationship': pointOfContactFormValues.relationship_to_deceased,
				};
			}
		}

		const childGuid = formValues.selectedChildId;

		const successQuery = this.app === DemographicAppKeys.myCPG ? FormTypes.DEATH_OF_CHILD : FormTypes.MAP_DEATH_OF_CHILD;
		const deathOfChildSuccess = () => {
			this.dispatch(removeChild(childGuid, this.app));
			formikHelpers.setValues(this.getInitialFormValue());
			formikHelpers.setTouched({});
			setShowPointOfContact(false);
			this.history.push(`${relationShipPath}?success=${successQuery}`);
		};

		const requestFailed = () => { };

		const deathRequest = (payload) => {
			this.dispatch(
				patchDeath(
					childGuid,
					payload,
					() => {
						deathOfChildSuccess();
					},
					requestFailed));
		};

		const onDocUploadSuccess = (changes: any) => (data) => {
			const withDocuments = appendDocument(changes, data?.map(a => a?.data?.doc_id));
			deathRequest(withDocuments);
		};

		// if docs to upload, upload them, then do the patch with the doc information
		if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0) {
			processRequestWithDocumentUpload(docUploadState, childGuid, this.dispatch, onDocUploadSuccess(initialPayload), requestFailed);
		} else {
			deathRequest(initialPayload);
		}
	}

	public onSubmit(
		values: DeathOfChildFormModel,
		formikHelpers: FormikProps<DeathOfChildFormModel>,
		guid: string,
		docUploadState: DocumentUploadState,
		relationShipPath: string,
		setShowPointOfContact: (confirm: boolean) => void,
		pointOfContactFormValues?: PointOfContactFormModel,
	) {

		this.dispatch(apiErrorClear());

		this.onDeath(values, formikHelpers, guid, docUploadState, relationShipPath, setShowPointOfContact, pointOfContactFormValues);
	}

	public shouldDisableSubmit(values: DeathOfChildFormModel) {
		return !values?.checkbox1;
	}

	public shouldShowDefaultDocumentUpload(): boolean {
		return true;
	}
}

